import React, { useMemo, createRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { 
    EditorState, 
    ContentState,
    convertToRaw
} from 'draft-js';
import moment from 'moment-timezone'
import { 
    motion, 
    AnimatePresence, 
    useSpring,
    useTransform, 
    useMotionValue,
    useViewportScroll,
} from "framer-motion"
import _ from 'lodash'

import { ItemBlock, InputBlock, CalendarBlock } from '../../components'
import { Animation } from 'components'

import { Item } from 'schema'
import { itemContext } from 'context'

const Container = styled.div`
    height: 100%;
    width: 100%;
    /* border: 1px solid greenyellow; */
`

const ItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`

const Layout = styled.div`
    /* display: grid;
    grid-template-columns: 50% 50%;
    grid-column-gap: 0.5rem; */
    height: 100%;
    @media (max-width: 600px) {
        display: flex;
        flex-direction: column;
    }
    /* border: 1px solid red; */
`

const ListPreview = props => {
    const {
        progress
    } = props
    let itemBlocks = []
    const timezone = moment.tz.guess();
    const items = [
        {
            content: 'Meeting with John about Q2 priorities',
            start_date: moment().tz(timezone),
            end_date: moment().tz(timezone),
            start_time: moment().tz(timezone).set({hour: 10, minute: 0}),
            end_time: moment().tz(timezone).set({hour: 11, minute: 0}),
            status: itemContext.STATUS.INCOMPLETE,
            priority: itemContext.PRIORITY.NONE,
        },
        {
            content: 'Revamped design for integrations feature',
            priority: itemContext.PRIORITY.NONE,
            tags: [{content: 'Feature', color: '#ea4861'}],
            children: progress > 0.20 ? {completed: 0, total: 2}
                    : progress > 0.10 ? {completed: 0, total: 1}
                    : null
        },
        {
            content: 'Prepare slides for weekly update',
            start_date: progress > 0.65 && moment().tz(timezone),
            end_date: progress > 0.65 && moment().tz(timezone),
            start_time: progress > 0.65 && moment().tz(timezone).set({hour: 8, minute: 0}),
            end_time: progress > 0.65 && moment().tz(timezone).set({hour: 10, minute: 0}),
            status: itemContext.STATUS.INCOMPLETE,
            priority: itemContext.PRIORITY.NONE,
        },
        {
            content: 'Lunch with Jane',
            start_date: moment().tz(timezone),
            end_date: moment().tz(timezone),
            start_time: moment().tz(timezone).set({hour: 12, minute: 0}),
            end_time: moment().tz(timezone).set({hour: 13, minute: 0}),
            status: itemContext.STATUS.INCOMPLETE,
            priority: itemContext.PRIORITY.NONE,
        },
        {
            content: 'Revamped design for integrations feature',
            priority: itemContext.PRIORITY.HIGH,
            start_date: moment().tz(timezone),
            end_date: moment().tz(timezone),
            start_time: moment().tz(timezone).set({hour: 13, minute: 0}),
            end_time: moment().tz(timezone).set({hour: 15, minute: 0}),
            status: itemContext.STATUS.INCOMPLETE
        }
    ]
    const events = [
        {
            start: items[0]['start_time'],
            end: items[0]['end_time'],
            allDay: false,
            title: 'Meeting with John about Q2 priorities',
            item: items[0]
        },
        {
            start: moment().tz(timezone).set({hour: 8, minute: 0}),
            end: moment().tz(timezone).set({hour: 10, minute: 0}),
            allDay: false,
            title: 'Prepare slides for weekly update',
            item: items[2],
        },
        {
            start: moment().tz(timezone).set({hour: 12, minute: 0}),
            end: moment().tz(timezone).set({hour: 13, minute: 0}),
            allDay: false,
            title: 'Lunch with Jane',
            item: items[3],
        },
        {
            start: moment().tz(timezone).set({hour: 13, minute: 0}),
            end: moment().tz(timezone).set({hour: 15, minute: 0}),
            allDay: false,
            title: 'Revamped design for integrations feature',
            item: items[4],
        }
    ]


    const refs= useMemo(
        () => Array.from({ length: 6 }).map(() => createRef()),[]
    );
    const layoutRef = React.useRef()
    let layoutRect = {x: 0, y: 0}
    useEffect(() => {
        layoutRect = layoutRef.current && {x: layoutRef.current.getBoundingClientRect()['left'], y: layoutRef.current.getBoundingClientRect()['top']}
    }, [progress])



    /////////////////
    // Controllers //
    /////////////////
    const getItem = (item, index, animate, end) => {
        let updatedItem = Object.assign({}, {
            content: '',
            priority: itemContext.PRIORITY.NONE,
            status: itemContext.STATUS.INCOMPLETE,
            offset: 0
        }, {...item})
        updatedItem['content'] = JSON.stringify(convertToRaw(EditorState.createWithContent(ContentState.createFromText(item['content'])).getCurrentContent()))
        return (
            <div style={{width: '100%'}} ref={refs[index]}>
                <div style={{display: 'flex', justifyContent:'flex-end'}} data-aos="fade-up">
                    <ItemBlock item={updatedItem} animateProps={animate}/>
                </div>
            </div>
        )
        
    }

    ///////////////////
    // Create items //
    //////////////////
    // Render items

    // Add items
    progress > 0.10 && itemBlocks.push(getItem(items[0], 0, false))
    progress > 0.30 && itemBlocks.push(getItem(items[1], 1, false))
    progress > 0.50 && itemBlocks.push(getItem(items[2], 2, true, 7))

    // Add events
    let eventBlocks = [events[0]]
    progress > 0.65 && eventBlocks.push(events[1])
    progress > 0.75 && eventBlocks.push(events[2])
    progress > 0.85 && eventBlocks.push(events[3])



    return (
        <Container>
            {/* <div>{progress}</div> */}
            <Layout ref={layoutRef}>
                <ItemContainer>
                    {itemBlocks}
                    <InputBlock item={{content: EditorState.createEmpty(), priority: itemContext.PRIORITY.NONE, offset: 0}}/>
                </ItemContainer>

                {progress > 0.60 &&
                <div data-aos="fade-up" style={{height: '50vh'}}>
                    <CalendarBlock events={eventBlocks} date={moment.tz(timezone).set({hour: 7, minute: 0})} scrollToTime={moment.tz(timezone).set({hour: 7, minute: 0})}/>    
                </div>}              
            </Layout>

            
        </Container>
    )
}

ListPreview.propTypes = {
    progress: PropTypes.number
}

export default ListPreview